import { useTranslation } from 'react-i18next';

import { ReactComponent as CaffeeInActive } from '../../../../assets/icons/amenities/caffee-inactive.svg';
import { ReactComponent as CarwashInActive } from '../../../../assets/icons/amenities/carwash-inactive.svg';
import { ReactComponent as GasStationInActive } from '../../../../assets/icons/amenities/gas-station-inactive.svg';
import { ReactComponent as LockerInActive } from '../../../../assets/icons/amenities/locker-inactive.svg';
import { ReactComponent as LoungeInActive } from '../../../../assets/icons/amenities/lounge-inactive.svg';
import { ReactComponent as RelaxingInActive } from '../../../../assets/icons/amenities/relaxing-inactive.svg';
import { ReactComponent as RestaurantInActive } from '../../../../assets/icons/amenities/restaurant-inactive.svg';
import { ReactComponent as RestroomInActive } from '../../../../assets/icons/amenities/restroom-inactive.svg';
import { ReactComponent as ShoppingInActive } from '../../../../assets/icons/amenities/shopping-inactive.svg';
import { ReactComponent as WifiInActive } from '../../../../assets/icons/amenities/wifi-inactive.svg';
import { ReactComponent as WorkshopInActive } from '../../../../assets/icons/amenities/workshop-inactive.svg';
import { ReactComponent as RemoveIcon } from '../../../../assets/icons/remove-icon.svg';
import { brandIcons } from '../../../../config/branding';
import { Amenity } from '../../../../interface/partner-interfaces/location-management.interface';

interface SelectedAmenitiesProperties {
  selectedAmenities: Amenity[];
  handleStationDetailChange: (name: string, changeValue: Amenity[]) => void;
  hideButtons?: boolean;
  hideRemove?: boolean;
  dynamicAmenities?: Amenity[];
}

interface AmenitiesDetailsProperties {
  name: string;
  key: Amenity;
  svgIconActive: JSX.Element;
  svgIconInActive: JSX.Element;
}

/**
 * Selected amenities popup
 * @param {any} properties - The properties
 */
function SelectAmenitiesV2(properties: SelectedAmenitiesProperties) {
  const {
    selectedAmenities,
    handleStationDetailChange,
    hideButtons,
    hideRemove,
    dynamicAmenities,
  } = properties;
  const { t, i18n } = useTranslation('location');

  const amenitiesDetails: AmenitiesDetailsProperties[] = [
    {
      name: t('gasStation'),
      key: 'Gas Station',
      svgIconActive: <brandIcons.GasStationActive />,
      svgIconInActive: <GasStationInActive />,
    },
    {
      name: t('wifi'),
      key: 'Free Wifi',
      svgIconActive: <brandIcons.WifiActive />,
      svgIconInActive: <WifiInActive />,
    },
    {
      name: t('restroom'),
      key: 'Restroom',
      svgIconActive: <brandIcons.RestroomActive />,
      svgIconInActive: <RestroomInActive />,
    },
    {
      name: t('Lounge'),
      key: 'Lounge',
      svgIconActive: <brandIcons.LoungeActive />,
      svgIconInActive: <LoungeInActive />,
    },
    {
      name: t('cafe'),
      key: 'Cafe',
      svgIconActive: <brandIcons.CaffeeActive />,
      svgIconInActive: <CaffeeInActive />,
    },
    {
      name: t('shopping'),
      key: 'Shopping',
      svgIconActive: <brandIcons.ShoppingActive />,
      svgIconInActive: <ShoppingInActive />,
    },
    {
      name: t('restaurant'),
      key: 'Restaurant',
      svgIconActive: <brandIcons.RestaurantActive />,
      svgIconInActive: <RestaurantInActive />,
    },
    {
      name: t('relaxingArea'),
      key: 'Relaxing Area',
      svgIconActive: <brandIcons.RelaxingActive />,
      svgIconInActive: <RelaxingInActive />,
    },
    {
      name: t('locker'),
      key: 'Locker',
      svgIconActive: <brandIcons.LockerActive />,
      svgIconInActive: <LockerInActive />,
    },
    {
      name: t('workshop'),
      key: 'Work Shop',
      svgIconActive: <brandIcons.WorkshopActive />,
      svgIconInActive: <WorkshopInActive />,
    },
    {
      name: t('carWash'),
      key: 'Car Wash',
      svgIconActive: <brandIcons.CarwashActive />,
      svgIconInActive: <CarwashInActive />,
    },
  ];

  /**
   * Toggle amenities selection
   * @param {any} amenity - The amenity
   */
  const toggleAmenitiesSelection = (amenity: Amenity) => {
    if (!hideButtons) {
      let nextAmenities: Amenity[] = [];
      if (selectedAmenities?.includes(amenity)) {
        // Remove amenity from the array
        nextAmenities = selectedAmenities.filter((each) => each !== amenity);
      } else {
        // Add amenity to the array
        nextAmenities = [...(selectedAmenities || []), amenity];
      }
      handleStationDetailChange('amenities', nextAmenities);
    }
  };

  const displayAmenities =
    dynamicAmenities && dynamicAmenities.length > 0
      ? amenitiesDetails.filter((amenity) => dynamicAmenities.includes(amenity.key))
      : amenitiesDetails;

  return (
    <div className="select-amenities-wrap versionV2" dir={i18n.dir()}>
      <div className="select-amenities-container versionV2">
        {displayAmenities?.map((amenity) => (
          <div
            className="amenity-wrap"
            key={amenity.key}
            role="presentation"
            onClick={() => toggleAmenitiesSelection(amenity.key)}>
            <div
              className={`amenity-icon ${selectedAmenities?.includes(amenity.key) ? 'active' : ''}`}>
              <span
                className={`active-icon ${selectedAmenities?.includes(amenity.key) ? 'view' : 'hide'}`}>
                {amenity.svgIconActive}
              </span>
              <span
                className={`inactive-icon ${!selectedAmenities?.includes(amenity.key) ? 'view' : 'hide'}`}>
                {amenity.svgIconInActive}
              </span>
              {!hideRemove && selectedAmenities?.includes(amenity.key) && (
                <div
                  className="remove-icon"
                  role="presentation"
                  onClick={(event) => {
                    event.stopPropagation();
                    toggleAmenitiesSelection(amenity.key);
                  }}>
                  <RemoveIcon />
                </div>
              )}
            </div>
            <p>{amenity.name}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default SelectAmenitiesV2;
